/**
 * The customer a Super Admin is creating a job for.
 *
 * A Super Admin belongs to no customer, so unlike every other role the backend
 * takes the customer from the request (SuperAdminJobStrategy). Until this field
 * existed the create-job form sent none, and the backend answered a Super
 * Admin's "Post job" with "Unexpected server error" (found 2026-10-02). The
 * permission that gates this, "Customer field on the post-job form", had been
 * defined and listed on the role-permissions card without anything rendering it.
 *
 * Reads GET /api/customers (Super Admin only). Never blank: loading, a failed
 * load with a retry, and "no customers" each say what they are.
 */
import { memo, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Select } from 'antd';
import axios from '../../../../axiosConfig';
import { customerLabel } from '../../../settings/utils/customerLabel';

interface CustomerOption {
  id: number;
  name: string;
  /** A test, E2E or demo tenant: say so, so a job isn't posted to one by mistake. */
  isSynthetic?: boolean;
}

interface Props {
  value?: number;
  onChange: (customerId?: number) => void;
  /** Show the "required" message, after a post was attempted without one. */
  showRequired?: boolean;
}

const JobCustomerField = ({ value, onChange, showRequired = false }: Props) => {
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    axios
      .get<CustomerOption[]>('/api/customers')
      .then((res) => {
        if (cancelled) return;
        if (!Array.isArray(res.data)) {
          setFailed(true);
          return;
        }
        setCustomers(res.data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  if (failed) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load customers, so a job can't be posted yet."
        action={
          <Button size="small" onClick={load}>
            Try again
          </Button>
        }
        style={{ marginBottom: 16 }}
      />
    );
  }

  return (
    <Form.Item
      label="Customer"
      required
      validateStatus={showRequired && value === undefined ? 'error' : undefined}
      help={
        showRequired && value === undefined
          ? 'Choose the customer this job is for.'
          : !loading && customers.length === 0
            ? 'There are no customers yet. Create one in Settings first.'
            : undefined
      }
    >
      <Select
        showSearch
        optionFilterProp="label"
        placeholder="Choose the customer this job is for"
        loading={loading}
        value={value}
        onChange={(id: number) => onChange(id)}
        options={customers.map((c) => ({
          label: customerLabel(c),
          value: c.id,
        }))}
        style={{ maxWidth: 480 }}
        aria-label="Customer"
      />
    </Form.Item>
  );
};

export default memo(JobCustomerField);
